/**
 * useRecoveryProof — fecha o loop da retenção ("seu toque trouxe aluno de volta").
 *
 * Busca no Supabase (notification_log + workout_sessions, só leitura) e delega o
 * cálculo pra lib/recovery.ts (lógica pura, testável). Reusa a RLS do PT.
 * Retorna { janelaDias, notificados, voltaram, taxa } — undefined enquanto carrega.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  DAY_MS,
  PRE_WINDOW_DAYS,
  buildFirstNudge,
  computeRecoveryProof,
  LogRow,
  SessionRow,
  RecoveryProof,
} from '@/lib/recovery';

export type { RecoveryProof } from '@/lib/recovery';

export function useRecoveryProof(personalId: string, janelaDias = 30) {
  return useQuery({
    queryKey: ['recovery-proof', personalId, janelaDias],
    queryFn: async (): Promise<RecoveryProof> => {
      const nowMs = Date.now();
      const cutoffIso = new Date(nowMs - janelaDias * DAY_MS).toISOString();

      // 1) Notificações do PT na janela
      const { data: logs, error: lErr } = await supabase
        .from('notification_log')
        .select('student_ids, sent_at')
        .eq('personal_id', personalId)
        .gte('sent_at', cutoffIso);
      if (lErr) throw lErr;

      const logRows = (logs ?? []) as LogRow[];
      const firstNudge = buildFirstNudge(logRows, nowMs - janelaDias * DAY_MS);
      const notifiedIds = Object.keys(firstNudge);
      if (notifiedIds.length === 0) {
        return { janelaDias, notificados: 0, voltaram: 0, taxa: 0 };
      }

      // 2) Sessões desses alunos a partir de 7d antes do toque mais antigo
      const minNudge = Math.min(...Object.values(firstNudge));
      const sinceIso = new Date(minNudge - PRE_WINDOW_DAYS * DAY_MS).toISOString();
      const { data: sessions, error: sErr } = await supabase
        .from('workout_sessions')
        .select('student_id, completed_at')
        .in('student_id', notifiedIds)
        .gte('completed_at', sinceIso)
        .not('completed_at', 'is', null);
      if (sErr) throw sErr;

      return computeRecoveryProof(logRows, (sessions ?? []) as SessionRow[], janelaDias, nowMs);
    },
    enabled: !!personalId,
    staleTime: 60 * 1000,
  });
}
